import { Dimensions, GoodsInfo } from '../model/GoodsInformation';

/**
 * 数値をフォーマットして文字列として返す
 * @param value フォーマットする数値
 * @returns フォーマットされた文字列
 */
export function formatNumber(value: number): string {
    return Number.isFinite(value) ? value.toLocaleString() : 'ー';
}

/**
 * 寸法から体積を計算する。寸法が未設定の場合はnullを返す
 * @param goodsInfo
 * @returns 体積（cm³）またはnull
 */
export function calculateVolumeCm3(goodsInfo: GoodsInfo): number | null {
    const dims = goodsInfo?.dimensions;
    if (!dims) return null;
    return (dims.length) * (dims.width) * (dims.height);
}

/**
 * 寸法からサイズラベルを取得する。
 * @param goodsInfo
 * @returns サイズラベル文字列
 */
export function getSizeLabel(goodsInfo: GoodsInfo): string {
    const dims = goodsInfo.dimensions;
    if (!dims) return 'ー';
    if (calculateVolumeCm3(goodsInfo) == null) return 'ー';
    const { length, width, height } = dims as Dimensions;
    return `${length}cm × ${width}cm × ${height}cm`;
}

/**
 * 体積ラベルを取得する。寸法が未設定の場合はプレースホルダ(ー)を返す
 * @param goodsInfo
 * @returns 体積ラベル文字列
 */
export function getVolumeLabel(goodsInfo: GoodsInfo): string {
    const perItem = calculateVolumeCm3(goodsInfo);
    if (perItem == null) return 'ー';
    return `${formatNumber(perItem * goodsInfo.count)} cm³`
}

/**
 * 重量ラベルを取得する
 * @param goodsInfo
 * @returns 重量ラベル文字列
 */
export function getWeightLabel(goodsInfo: GoodsInfo): string {
    const totalWeight = goodsInfo.weight * goodsInfo.count;
    return `${formatNumber(totalWeight)} g`;
}

/**
 * 総重量を計算する。goodsが無効な場合は0を返す
 * @param goodsInfoList
 * @returns 総重量（g）
 */
export function sumTotalWeight(goodsInfoList: GoodsInfo[]): number {
    return goodsInfoList.reduce((sum, goodsInfo) => {
        return sum + goodsInfo.weight * goodsInfo.count;
    }, 0);
}

/**
 * 総重量ラベルを取得する
 * @param goodsInfoList
 * @returns 総重量ラベル文字列
 */
export function getTotalWeightLabel(goodsInfoList: GoodsInfo[]): string {
    return `${formatNumber(sumTotalWeight(goodsInfoList))} g`;
}

/**
 * 総体積を計算する。goodsが無効な場合は0を返す。
 * 寸法未設定の荷物がある場合は、その荷物をスキップして計算する。
 * @param goodsInfoList
 * @returns 総体積（cm³）
 */
export function sumTotalVolumeCm3(
    goodsInfoList: Array<GoodsInfo>
): number {
    let total = 0;
    for (const goodsInfo of goodsInfoList) {
        const perItem = calculateVolumeCm3(goodsInfo);
        if (perItem == null) {
            // 寸法未設定の荷物はスキップ
            continue;
        }
        total += perItem * goodsInfo.count;
    }
    return total;
}

/**
 * 総体積ラベルを取得する
 * @param goodsInfoList
 * @returns 総体積ラベル文字列
 */
export function getTotalVolumeLabel(goodsInfoList: Array<GoodsInfo>): string {
    return `${formatNumber(sumTotalVolumeCm3(goodsInfoList))} cm³`;
}